
import React, { useState, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, X, FileText, File, Image, FileIcon, MoreHorizontal, Eye, Download, Printer } from "lucide-react";
import { Document } from "@/services/db/types";
import { toast } from 'sonner';
import { useDocumentManagement } from '@/hooks/useDocumentManagement';
import { v4 as uuidv4 } from 'uuid';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const StudentDocumentsUpload: React.FC = () => {
  const form = useFormContext();
  const [documents, setDocuments] = useState<Document[]>(form.watch("documents") || []);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const studentId = form.watch("id");
  const { trackDocumentAction } = useDocumentManagement(studentId || 0);

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!file || file.size === 0) {
        reject(new Error('Invalid or empty file'));
        return;
      }
      
      console.log(`📤 Converting file: ${file.name} (${file.size} bytes, ${file.type})`);
      
      const reader = new FileReader();
      
      reader.onload = () => {
        const result = reader.result as string;
        if (result && result.length > 0) {
          console.log(`✅ File converted successfully: ${result.length} characters`);
          resolve(result);
        } else {
          reject(new Error('Failed to convert file - empty result'));
        }
      };
      
      reader.onerror = () => {
        console.error('FileReader error:', reader.error);
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(file);
    });
  };
  
  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <File className="h-5 w-5 text-red-500" />;
    if (fileType.includes('image')) return <Image className="h-5 w-5 text-blue-500" />;
    if (fileType.includes('word') || fileType.includes('doc')) return <FileText className="h-5 w-5 text-blue-700" />;
    return <FileIcon className="h-5 w-5 text-gray-500" />;
  };
  
  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    console.log(`📤 Starting upload for: ${file.name} (${file.size} bytes, ${file.type})`);
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Maximum file size is 10MB");
      return;
    }
    
    if (file.size === 0) {
      toast.error("Cannot upload empty file");
      return;
    }
    
    try {
      const base64Data = await convertFileToBase64(file);
      
      if (!base64Data || base64Data.length === 0) {
        throw new Error('File conversion failed - no data');
      }
      
      const documentId = uuidv4();
      const timestamp = new Date().toISOString();
      
      // Create document with proper data storage - ENSURE url and size are properly set
      const newDocument: Document = {
        id: documentId,
        name: file.name,
        type: file.type,
        url: base64Data, // Store the full base64 data URL here
        uploadDate: timestamp,
        uploadedBy: 'user',
        verificationStatus: 'pending',
        size: file.size, // Store the actual file size
        notes: ''
      };
      
      console.log(`💾 Creating document with data:`, {
        name: newDocument.name,
        size: newDocument.size,
        urlLength: newDocument.url?.length || 0,
        type: newDocument.type,
        hasValidUrl: !!(newDocument.url && newDocument.url.length > 0),
        hasValidSize: !!(newDocument.size && newDocument.size > 0)
      });
      
      // Remove any existing document with same name to prevent duplicates
      const filteredDocuments = documents.filter(doc => doc.name !== file.name);
      const updatedDocuments = [...filteredDocuments, newDocument];
      
      setDocuments(updatedDocuments);
      form.setValue("documents", updatedDocuments);
      
      // Track document action
      try {
        if (studentId) {
          await trackDocumentAction(documentId, 'upload', newDocument);
        }
      } catch (error) {
        console.warn("Warning: Could not track document upload:", error);
      }
      
      toast.success(`✅ ${file.name} uploaded successfully (${formatFileSize(file.size)})`);
      console.log(`✅ Document upload completed: ${newDocument.name} with ${newDocument.size} bytes and ${newDocument.url.length} chars URL`);
      
    } catch (error) {
      console.error("❌ Error uploading document:", error);
      toast.error(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };
  
  const removeDocument = async (index: number) => {
    const documentToRemove = documents[index];
    const documentName = documentToRemove.name;
    
    try {
      if (studentId && documentToRemove.id) {
        await trackDocumentAction(documentToRemove.id, 'delete', documentToRemove);
      }
    } catch (error) {
      console.warn("Warning: Could not track document deletion:", error);
    }
    
    const updatedDocuments = [...documents];
    updatedDocuments.splice(index, 1);
    setDocuments(updatedDocuments);
    form.setValue("documents", updatedDocuments);
    
    toast.success(`${documentName} has been removed`);
  };

  const handleViewDocument = (doc: Document) => {
    console.log(`👁️ Viewing document: ${doc.name} (Size: ${doc.size || 0} bytes, URL length: ${doc.url?.length || 0})`);
    setSelectedDocument(doc);
    setIsViewerOpen(true);
  };

  const handleDownloadDocument = (doc: Document) => {
    if (!doc.url || doc.url.trim() === '') {
      toast.error('Document data not available for download');
      return;
    }
    
    try {
      const link = document.createElement('a');
      link.href = doc.url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handlePrintDocument = (doc: Document) => {
    if (!doc.url || doc.url.trim() === '') {
      toast.error('Document data not available for printing');
      return;
    }

    try {
      if (doc.type?.startsWith('image/')) {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Print Document - ${doc.name}</title>
                <style>
                  body { margin: 0; padding: 20px; text-align: center; font-family: Arial, sans-serif; }
                  img { max-width: 100%; max-height: 90vh; object-fit: contain; }
                  h2 { color: #3D7069; margin-bottom: 20px; }
                  .no-print { margin-bottom: 20px; }
                  @media print { 
                    body { padding: 0; }
                    .no-print { display: none; }
                  }
                </style>
              </head>
              <body>
                <div class="no-print">
                  <button onclick="window.print()" style="padding: 10px 20px; margin-right: 10px; background: #3D7069; color: white; border: none; border-radius: 4px; cursor: pointer;">Print</button>
                  <button onclick="window.close()" style="padding: 10px 20px; background: #6b7280; color: white; border: none; border-radius: 4px; cursor: pointer;">Close</button>
                </div>
                <h2>${doc.name}</h2>
                <img src="${doc.url}" alt="${doc.name}" />
              </body>
            </html>
          `);
          printWindow.document.close();
          setTimeout(() => printWindow.focus(), 100);
        }
      } else {
        window.open(doc.url, '_blank');
      }
      toast.success('Opening document for printing');
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to open document for printing');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  const handleAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Simplified validation - if document has URL and size, it's valid
  const isValidDocument = (doc: Document) => {
    return !!(doc.url && doc.url.length > 0 && doc.size && doc.size > 0);
  };
  
  return (
    <>
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Documents</h3>
        <p className="text-sm text-muted-foreground">
          Upload certificates, diplomas, or any other relevant documents
        </p>
        
        <FormField
          control={form.control}
          name="documents"
          render={() => (
            <FormItem>
              <FormLabel>Upload Documents</FormLabel>
              <div 
                className="flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" 
                onClick={handleAreaClick}
              >
                <div className="text-center">
                  <Upload className="mx-auto h-10 w-10 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    PDF, DOCX, JPG, PNG and other documents (max 10MB)
                  </p>
                  <Input
                    id="document-upload"
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.jpg,.jpeg,.png,.xls,.xlsx,.txt,.rtf,.ppt,.pptx"
                    className="hidden"
                    onChange={handleDocumentUpload}
                  />
                </div>
              </div>
              
              {documents.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm font-medium">Uploaded Documents</h4>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg divide-y">
                    {documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-4">
                        <div className="flex items-center space-x-3 flex-1">
                          {getFileIcon(doc.type)}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate max-w-[200px]">{doc.name}</div>
                            <div className="text-xs text-gray-500">
                              {formatFileSize(doc.size || 0)}
                            </div>
                            <div className="text-xs text-green-600 mt-1">
                              ✓ Document ready for viewing
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32 bg-white dark:bg-gray-800 border shadow-lg z-50">
                              <DropdownMenuItem onClick={() => handleViewDocument(doc)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadDocument(doc)}>
                                <Download className="h-4 w-4 mr-2" />
                                Download
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handlePrintDocument(doc)}>
                                <Printer className="h-4 w-4 mr-2" />
                                Print
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => removeDocument(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Document Viewer Dialog */}
      <Dialog open={isViewerOpen} onOpenChange={setIsViewerOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] w-[90vw] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {selectedDocument && getFileIcon(selectedDocument.type)}
              <span className="truncate">{selectedDocument?.name}</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {selectedDocument && (
              <div className="bg-muted p-3 rounded-lg flex-shrink-0 mb-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-muted-foreground">Type:</span>
                    <p className="font-medium">{selectedDocument.type || 'Unknown'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-muted-foreground">Size:</span>
                    <p className="font-medium">{formatFileSize(selectedDocument.size || 0)}</p>
                  </div>
                  <div>
                    <span className="font-medium text-muted-foreground">Added:</span>
                    <p className="font-medium">
                      {selectedDocument.uploadDate ? new Date(selectedDocument.uploadDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex-1 border rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-900 flex items-center justify-center min-h-[400px]">
              {selectedDocument && selectedDocument.url && selectedDocument.url.length > 0 ? (
                selectedDocument.type?.includes('image') ? (
                  <div className="w-full h-full p-4 overflow-auto flex items-center justify-center">
                    <img 
                      src={selectedDocument.url} 
                      alt={selectedDocument.name}
                      className="max-w-full max-h-full object-contain rounded shadow-lg"
                      style={{ maxHeight: 'calc(100vh - 300px)' }}
                    />
                  </div>
                ) : selectedDocument.type?.includes('pdf') ? (
                  <div className="w-full h-full">
                    <iframe 
                      src={`${selectedDocument.url}#toolbar=1&navpanes=1&scrollbar=1`}
                      className="w-full h-full border-0"
                      title={selectedDocument.name}
                      style={{ minHeight: '400px' }}
                    />
                  </div>
                ) : (
                  <div className="text-center p-8">
                    <FileText className="h-20 w-20 mx-auto mb-6 text-gray-400" />
                    <h3 className="text-xl font-semibold mb-2">Document Preview</h3>
                    <p className="text-muted-foreground mb-6">
                      This document type cannot be previewed directly in the browser
                    </p>
                    <Button onClick={() => handleDownloadDocument(selectedDocument)} size="lg">
                      <Download className="h-5 w-5 mr-2" />
                      Download to View
                    </Button>
                  </div>
                )
              ) : (
                <div className="text-center p-8">
                  <FileText className="h-20 w-20 mx-auto mb-6 text-orange-300" />
                  <h3 className="text-xl font-semibold mb-2 text-orange-600">Document Data Missing</h3>
                  <p className="text-muted-foreground mb-4">This document was not uploaded properly</p>
                  <p className="text-sm text-gray-500">Please try re-uploading the document</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsViewerOpen(false)}>
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
            {selectedDocument && selectedDocument.url && selectedDocument.url.length > 0 && (
              <>
                <Button variant="outline" onClick={() => handleDownloadDocument(selectedDocument)}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <Button onClick={() => handlePrintDocument(selectedDocument)}>
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StudentDocumentsUpload;
